import { useEffect, useState } from "react";
import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  LayoutDashboard, Users, KanbanSquare, CalendarDays, Workflow,
  BarChart3, Bell, CreditCard, Settings, LogOut, Menu, X, Droplets, UserCircle,
} from "lucide-react";

const NAV = [
  { to: "/app/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/app/leads", label: "Leads", icon: KanbanSquare, testid: "nav-leads" },
  { to: "/app/customers", label: "Customers", icon: Users, testid: "nav-customers" },
  { to: "/app/appointments", label: "Appointments", icon: CalendarDays, testid: "nav-appointments" },
  { to: "/app/automations", label: "Automations", icon: Workflow, testid: "nav-automations" },
  { to: "/app/analytics", label: "Analytics", icon: BarChart3, testid: "nav-analytics" },
  { to: "/app/notifications", label: "Notifications", icon: Bell, testid: "nav-notifications" },
  { to: "/app/billing", label: "Billing", icon: CreditCard, testid: "nav-billing" },
  { to: "/app/settings", label: "Settings", icon: Settings, testid: "nav-settings" },
];

export default function Layout() {
  const { user, business, subscription, unread, setUnread, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    api.get("/notifications").then(({ data }) => setUnread(data.unread)).catch(() => {});
  }, [setUnread]);

  const initials = (user?.name || "U").split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase();

  const statusColor = {
    trialing: "bg-amber-100 text-amber-800 border-amber-200",
    active: "bg-emerald-100 text-emerald-800 border-emerald-200",
    past_due: "bg-red-100 text-red-800 border-red-200",
    canceled: "bg-slate-100 text-slate-700 border-slate-200",
    none: "bg-slate-100 text-slate-700 border-slate-200",
  }[subscription?.status || "none"];

  const SidebarInner = () => (
    <div className="flex flex-col h-full">
      <div className="h-16 flex items-center gap-2 px-6 border-b border-slate-800">
        <div className="h-9 w-9 rounded-lg bg-blue-600 flex items-center justify-center">
          <Droplets className="h-5 w-5 text-white" />
        </div>
        <div className="leading-tight">
          <div className="font-heading font-bold text-white text-lg">PlumbFlow</div>
        </div>
      </div>
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            data-testid={item.testid}
            onClick={() => setMobileOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                isActive ? "bg-blue-600 text-white" : "text-slate-300 hover:bg-slate-800 hover:text-white"
              }`
            }
          >
            <item.icon className="h-[18px] w-[18px]" />
            <span className="flex-1">{item.label}</span>
            {item.to === "/app/notifications" && unread > 0 && (
              <span className="bg-red-500 text-white text-xs rounded-full h-5 min-w-5 px-1 flex items-center justify-center">
                {unread}
              </span>
            )}
          </NavLink>
        ))}
      </nav>
      <div className="p-4 border-t border-slate-800">
        <div className="text-xs text-slate-400 truncate">{business?.name}</div>
        <Badge className={`mt-1 border ${statusColor}`} data-testid="sidebar-sub-status">
          {subscription?.status === "trialing" ? "Free Trial" : (subscription?.status || "none")}
        </Badge>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex w-64 bg-slate-900 flex-col fixed inset-y-0 z-30">
        <SidebarInner />
      </aside>

      {/* Mobile sidebar */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <aside className="relative w-64 bg-slate-900 flex flex-col">
            <button className="absolute top-4 right-4 text-slate-300" onClick={() => setMobileOpen(false)} data-testid="close-mobile-menu">
              <X className="h-5 w-5" />
            </button>
            <SidebarInner />
          </aside>
        </div>
      )}

      <div className="flex-1 md:ml-64 flex flex-col min-w-0">
        <header className="h-16 bg-white border-b border-slate-200 sticky top-0 z-20 flex items-center gap-3 px-4 sm:px-6">
          <button className="md:hidden text-slate-700" onClick={() => setMobileOpen(true)} data-testid="open-mobile-menu">
            <Menu className="h-6 w-6" />
          </button>
          <div className="flex-1" />
          <Button variant="ghost" size="icon" onClick={() => navigate("/app/notifications")} data-testid="topbar-notifications" className="relative">
            <Bell className="h-5 w-5" />
            {unread > 0 && <span className="absolute top-1 right-1 h-2.5 w-2.5 bg-red-500 rounded-full" />}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2" data-testid="user-menu-trigger">
                <Avatar className="h-9 w-9">
                  <AvatarFallback className="bg-blue-600 text-white text-sm">{initials}</AvatarFallback>
                </Avatar>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="font-medium">{user?.name}</div>
                <div className="text-xs text-slate-500 font-normal">{user?.email}</div>
                <Badge variant="secondary" className="mt-1 capitalize">{user?.role}</Badge>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate("/app/settings")} data-testid="menu-settings">
                <Settings className="h-4 w-4 mr-2" /> Settings
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/app/billing")} data-testid="menu-billing">
                <CreditCard className="h-4 w-4 mr-2" /> Billing
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout} data-testid="menu-logout" className="text-red-600">
                <LogOut className="h-4 w-4 mr-2" /> Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        <main className="flex-1 p-4 sm:p-6 lg:p-8 max-w-[1500px] w-full mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
